import { useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { setShowFormModal, setNavigateValue } from 'slices/userSlice'
import { setCurrentTab } from 'slices/customerSteps'

export const useUnsavedChangesWarning = (hasUnsavedChanges = false) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const pendingNavigationRef = useRef(null)

  const isAutoSaving = useSelector(state => state.user.isAutoSaving)
  const autoSaveField = useSelector(state => state.user.autoSaveField)
  const apiCallState = useSelector(state => state.apiCall)
  const pendingPath = useSelector(state => state.user.navigateValue)

  const requestNavigation = useCallback(
    to => {
      // Show modal if auto-save or any API call is in progress
      const isApiRunning = Object.values(apiCallState || {}).some(
        v => v === true
      )
      if (isAutoSaving || isApiRunning) {
        pendingNavigationRef.current = { pathname: to }
        dispatch(setNavigateValue({ navigateValue: to }))
        dispatch(setShowFormModal({ showFormModal: true }))
        return false
      }
      navigate(to)
      return true
    },
    [dispatch, isAutoSaving, navigate, apiCallState]
  )

  useEffect(() => {
    const handleBeforeUnload = e => {
      const isApiRunning = Object.values(apiCallState || {}).some(
        v => v === true
      )
      if ((hasUnsavedChanges || isApiRunning) && !isAutoSaving) {
        e.preventDefault()
        e.returnValue = ''
        return ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [hasUnsavedChanges, isAutoSaving, apiCallState])

  const proceedNavigation = useCallback(() => {
    const target = pendingNavigationRef.current?.pathname || pendingPath
    if (target) {
      if (typeof target === 'string' && target.startsWith('TAB:')) {
        const parts = target.split(':')[1]
        const tabNum = parseInt(parts, 10)
        if (!Number.isNaN(tabNum)) {
          dispatch(setCurrentTab(tabNum))
        }
      } else {
        navigate(target)
      }
    }

    pendingNavigationRef.current = null
    dispatch(setShowFormModal({ showFormModal: false }))
  }, [navigate, dispatch, pendingPath])

  const cancelNavigation = useCallback(() => {
    pendingNavigationRef.current = null
    dispatch(setShowFormModal({ showFormModal: false }))
  }, [dispatch])

  return {
    requestNavigation,
    proceedNavigation,
    cancelNavigation,
    isBlocked: Boolean(
      hasUnsavedChanges ||
        isAutoSaving ||
        Object.values(apiCallState || {}).some(
          v => v === true
        )
    )
  }
}

export default useUnsavedChangesWarning
